import { baseApi } from '@app/api/baseApi';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { useLogoutMutation } from '@features/auth/authApi';
import { logout } from '@features/auth/authSlice';
import {
	Edit,
	Logout,
	Menu as MenuIcon,
	MenuOpen,
	Notifications,
	Person,
} from '@mui/icons-material';
import {
	AppBar,
	Avatar,
	Badge,
	Box,
	Divider,
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	Toolbar,
	Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toggleSidebarCollapsed } from './uiSlice';

export function TopBar() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { user } = useAppSelector((state) => state.auth);
	const { sidebarCollapsed } = useAppSelector((state) => state.ui);
	const [logoutApi] = useLogoutMutation();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleLogout = async () => {
		handleMenuClose();
		try {
			// Call API to clear HttpOnly cookies on server
			await logoutApi().unwrap();
		} catch (error) {
			// Even if API fails, still logout locally
			console.error('Logout API error:', error);
		}
		// Clear Redux state
		dispatch(logout());
		// Clear RTK Query cache to prevent stale data
		dispatch(baseApi.util.resetApiState());
		navigate('/login');
	};

	const getInitials = (firstName?: string, lastName?: string) => {
		return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';
	};

	return (
		<AppBar
			position='sticky'
			color='inherit'
			elevation={0}
			sx={{
				borderBottom: '1px solid',
				borderColor: 'divider',
				bgcolor: 'background.paper',
			}}>
			<Toolbar>
				<IconButton
					edge='start'
					color='inherit'
					onClick={() => dispatch(toggleSidebarCollapsed())}
					sx={{ mr: 2 }}>
					{sidebarCollapsed ? <MenuIcon /> : <MenuOpen />}
				</IconButton>

				<Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
					{/* Page title can be added here */}
				</Typography>

				{/* Notifications */}
				<IconButton color='inherit' sx={{ mr: 1 }}>
					<Badge badgeContent={3} color='error'>
						<Notifications />
					</Badge>
				</IconButton>

				{/* User Menu */}
				<Box>
					<IconButton onClick={handleMenuOpen} size='small'>
						<Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
							{getInitials(user?.firstName, user?.lastName)}
						</Avatar>
					</IconButton>
					<Menu
						anchorEl={anchorEl}
						open={open}
						onClose={handleMenuClose}
						transformOrigin={{ horizontal: 'right', vertical: 'top' }}
						anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
						PaperProps={{
							sx: { minWidth: 200, mt: 1 },
						}}>
						<Box sx={{ px: 2, py: 1 }}>
							<Typography variant='subtitle1' fontWeight='bold'>
								{user?.firstName} {user?.lastName}
							</Typography>
							<Typography variant='body2' color='text.secondary'>
								{user?.email}
							</Typography>
						</Box>
						<Divider />
						<MenuItem
							onClick={() => {
								handleMenuClose();
								navigate(`/users/${user?.id}`);
							}}>
							<ListItemIcon>
								<Person fontSize='small' />
							</ListItemIcon>
							Профил
						</MenuItem>
						<MenuItem
							onClick={() => {
								handleMenuClose();
								navigate(`/users/${user?.id}/edit`);
							}}>
							<ListItemIcon>
								<Edit fontSize='small' />
							</ListItemIcon>
							Редакция
						</MenuItem>
						<Divider />
						<MenuItem onClick={handleLogout}>
							<ListItemIcon>
								<Logout fontSize='small' />
							</ListItemIcon>
							Изход
						</MenuItem>
					</Menu>
				</Box>
			</Toolbar>
		</AppBar>
	);
}
